import type {
  ApiReturnProject,
  ApiReturnSite,
} from '@highimpact-frontend/api-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import assert from 'assert';
import {
  JSXElementConstructor,
  ReactElement,
  useState,
  type LegacyRef,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import SiteLeftNavComponent from '../../../components/base/nav/SiteLeftNavComponent';
import TopNavBarComponent from '../../../components/base/nav/TopNavComponent';
import LanguageSelector from '../../../components/LanguageSelector';
import type { NavButtonProps } from '../../../components/utils/nav/NavButtonComponent';
import NavButtonComponent from '../../../components/utils/nav/NavButtonComponent';
import NavItemComponent from '../../../components/utils/nav/NavItemComponent';
import NavSectionComponent from '../../../components/utils/nav/NavSectionComponent';
import PermissionDecoratorV2 from '../../../components/utils/permission/PermissionDecoratorV2';
import { useCheckPermission } from '../../../components/utils/permission/helpers/usePermission';
import StateDecorator from '../../../components/utils/state/StateDecorator';
import { getProjectFromSites } from '../../../store/config';
import { isDataState } from '../../../store/data/state';
import { useAppSelector } from '../../../store/hooks';
import {
  DashboardContextProps,
  DashbordOnlyContextProps,
  useDashboardContext,
} from '../../../utils/hooks/useDashboardContext';

export const baseBackofficeNav: NavButtonProps[] = [
  {
    name: 'Projets',
    url: '/management/projects',
    itemName: 'configuration_project_tab',
  },
  {
    name: 'Sites',
    url: '/management/sites',
    itemName: 'configuration_site_tab',
  },
  {
    name: 'Inventaire des dispositifs de mesure',
    url: '/management/devices',
    itemName: 'configuration_devices_tab',
  },
  {
    name: 'Utilisateurs',
    url: '/management/users',
    itemName: 'configuration_users_tab',
  },
];

/**
 * Defines overloads of the function: the type of the render function determines the dashboardContext type
 */
interface SiteDecoratorSignature {
  // Full context or no context: we expect to provide the project and site to the rendering function
  (
    ref: LegacyRef<HTMLDivElement>,
    render: (project: ApiReturnProject, site: ApiReturnSite) => ReactNode,
    dashboardContext?: Exclude<DashboardContextProps, DashbordOnlyContextProps>
  ): ReactElement<any, string | JSXElementConstructor<any>>;
  // Just the dashboard id: we render without a project nor a site
  (
    ref: LegacyRef<HTMLDivElement>,
    render: () => ReactNode,
    dashboardContext: DashbordOnlyContextProps
  ): ReactElement<any, string | JSXElementConstructor<any>>;
}

const SiteDecorator: SiteDecoratorSignature = (
  ref,
  render,
  dashboardContext = { mode: 'nocontext' }
) => {
  const { t } = useTranslation('menu');
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const dashboard_only_mode = searchParams.get('dashboard_only_mode');
  const snapShotMode =
    (dashboard_only_mode !== undefined &&
      dashboard_only_mode !== null &&
      dashboard_only_mode.toLowerCase() !== 'false') ||
    location.pathname.startsWith('/public');
  const backofficeNav = baseBackofficeNav.map((e) => ({
    ...e,
    name: t(e.name),
  }));
  const navigate = useNavigate();

  const shouldRenderLeftNav =
    dashboardContext.mode === 'nocontext' && !snapShotMode;
  const shouldProvideProjectAndSite = dashboardContext.mode !== 'dashbordonly';

  const { site_id } = useDashboardContext(dashboardContext);

  // In dashboardonly mode, we don't expect the site_id to be provided
  if (dashboardContext.mode !== 'dashbordonly') {
    assert(site_id);
  }

  const projects = useAppSelector(
    (state) => state.data_slice.externals.projects
  );
  const sites = useAppSelector((state) => state.data_slice.externals.sites);
  const project_sites = useAppSelector(
    (state) => state.data_slice.externals.project_sites
  );

  const authentication = useAppSelector(
    (state) => state.data_slice.externals.authentication
  );

  const actions = useAppSelector((state) => state.data_slice.externals.actions);

  const [showLeftNav, setShowLeftNav] = useState(false);

  const {
    shouldHide: disableTopBarCollapse,
    canDisable: alsoDisableTopBarCollapse,
  } = useCheckPermission('top_bar_can_collapse');
  const shouldDisableCollapse =
    disableTopBarCollapse || alsoDisableTopBarCollapse;
  const { shouldHide: disableTopBarPin, canDisable: alsoDisableTopBarPin } =
    useCheckPermission('top_bar_can_be_pinned');
  const shouldDisablePin = disableTopBarPin || alsoDisableTopBarPin;
  const {
    shouldHide: disableCollapsedMenuButton,
    canDisable: alsoDisableCollapsedMenuButton,
  } = useCheckPermission('collapsed_menu_button_should_be_displayed');
  const shouldDisableCollapsedMenuButton =
    disableCollapsedMenuButton || alsoDisableCollapsedMenuButton;

  return StateDecorator(
    [sites, projects, project_sites, authentication, actions],
    ref,
    () => {
      assert(isDataState(sites));
      assert(isDataState(projects));
      assert(isDataState(project_sites));
      assert(isDataState(authentication));
      assert(isDataState(actions));
      const site = site_id ? sites.data[site_id] : undefined;
      const project = site
        ? getProjectFromSites(site, project_sites, projects)[0]
        : undefined;

      // In dashboardonly mode, we don't expect to have a project nor a site
      if (dashboardContext.mode !== 'dashbordonly') {
        assert(site);
        assert(project);
      }

      const logoElement = (
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <span className="nav-icon">
            <Box
              component="img"
              sx={{
                maxHeight: 48,
                width: 'auto',
                objectFit: 'contain',
              }}
              alt="favicon"
              src="/favicon.ico"
            />
          </span>
          <div>
            {t('HIGH IMPACT')}
            <div className="uk-navbar-subtitle">
              {t('GESTION ENVIRONNEMENTALE')}
            </div>
          </div>
        </Stack>
      );

      return (
        <main>
          <div>
            <TopNavBarComponent
              isPinnable={shouldDisablePin ? false : true}
              isCollapsible={shouldDisableCollapse ? false : true}
              barHeight={64}
              collapsedHeight={8}
              showCollapsedMenuButton={
                shouldDisableCollapsedMenuButton ? false : true
              }
              hideSiteNavigator={snapShotMode}
              hideToggleShowLeftNav={snapShotMode || !shouldRenderLeftNav}
              onToggleShowLeftNav={() => setShowLeftNav((bool) => !bool)}
              leftElement={
                snapShotMode ? (
                  <div>{logoElement}</div>
                ) : (
                  <Link to="/" className="uk-link-reset">
                    {logoElement}
                  </Link>
                )
              }
            >
              {!snapShotMode && (
                <NavSectionComponent>
                  <Stack direction="row" spacing={2}>
                    <NavItemComponent>
                      <LanguageSelector />
                    </NavItemComponent>
                    <PermissionDecoratorV2 item={'actions_button'}>
                      <NavItemComponent className="nav-item-static">
                        <div>
                          <div>
                            <button
                              className="uk-button-link"
                              type="button"
                              onClick={() => {
                                /** */
                              }}
                            >
                              {t('Actions')}
                            </button>
                            <div uk-dropdown="pos: bottom-center">
                              <ul className="uk-nav uk-dropdown-nav">
                                {Object.values(actions.data).map(
                                  ({ label, url }, key) => (
                                    <li key={key} className="uk-active">
                                      <Link
                                        to={url}
                                        className="uk-link-reset"
                                        style={{ flexGrow: 1 }}
                                      >
                                        {label}
                                      </Link>
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </NavItemComponent>
                    </PermissionDecoratorV2>
                    <NavItemComponent className="nav-item-static">
                      <PermissionDecoratorV2 item={'configuration_button'}>
                        <div className="dropdown-permission-wrapper">
                          <button
                            className="uk-button-link uk-text-small"
                            type="button"
                            onClick={() => navigate(backofficeNav[0].url)}
                          >
                            {t('Configurations')}
                          </button>

                          <div uk-dropdown="pos: bottom-center">
                            <ul className="uk-nav uk-dropdown-nav">
                              {backofficeNav.map(
                                ({ name, url, itemName }, key) => (
                                  <li key={key} className="uk-active">
                                    {itemName ? (
                                      <PermissionDecoratorV2 item={itemName}>
                                        <Link
                                          to={url}
                                          className="uk-link-reset"
                                          style={{ flexGrow: 1 }}
                                        >
                                          {name}
                                        </Link>
                                      </PermissionDecoratorV2>
                                    ) : (
                                      <Link
                                        to={url}
                                        className="uk-link-reset"
                                        style={{ flexGrow: 1 }}
                                      >
                                        {name}
                                      </Link>
                                    )}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        </div>
                      </PermissionDecoratorV2>
                    </NavItemComponent>

                    <NavItemComponent className="nav-item-static">
                      <PermissionDecoratorV2 item={'help_button'}>
                        <div className="dropdown-permission-wrapper">
                          <button
                            className="uk-button-link uk-text-small"
                            type="button"
                            onClick={() =>
                              window.open(
                                'https://high-impact.fr/#contact',
                                '_blank',
                                'noopener,noreferrer'
                              )
                            }
                          >
                            {t('Aide')}
                          </button>

                          <div uk-dropdown="pos: bottom-center">
                            <ul className="uk-nav uk-dropdown-nav">
                              <li className="uk-active">
                                <Link
                                  to="#"
                                  className="uk-link-reset"
                                  style={{ flexGrow: 1 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.open(
                                      'https://high-impact.fr/#contact',
                                      'noopener,noreferrer'
                                    );
                                  }}
                                >
                                  {t('Contact')}
                                </Link>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </PermissionDecoratorV2>
                    </NavItemComponent>

                    <NavItemComponent className="nav-item-static">
                      <PermissionDecoratorV2 item={'user_profile_button'}>
                        <div className="dropdown-permission-wrapper">
                          <button
                            className="uk-button-link uk-text-small"
                            type="button"
                          >
                            {authentication.data.name}
                          </button>

                          <div uk-dropdown="pos: bottom-center">
                            <ul className="uk-nav uk-dropdown-nav">
                              <NavButtonComponent
                                icon="sign-out"
                                name={t('Se déconnecter')}
                                url="/logout"
                                mode="danger"
                              />
                            </ul>
                          </div>
                        </div>
                      </PermissionDecoratorV2>
                    </NavItemComponent>
                  </Stack>
                </NavSectionComponent>
              )}
            </TopNavBarComponent>
            {shouldRenderLeftNav ? (
              // If we should render the left-side menu, we expect project and site to be defined
              <div className="dashboard_screen">
                <SiteLeftNavComponent
                  project_id={project!.id}
                  site_id={site!.id}
                  show={showLeftNav}
                  onToggleClick={() => setShowLeftNav((bool) => !bool)}
                />
                <div className="sites">{render(project!, site!)}</div>
              </div>
            ) : shouldProvideProjectAndSite ? (
              // Same assumption if these variables must be provided
              <div className="sites">{render(project!, site!)}</div>
            ) : (
              // Otherwise we expect the rendering function not to expect them
              <div className="sites">{(render as () => ReactNode)()}</div>
            )}
          </div>
        </main>
      );
    }
  );
};

export default SiteDecorator;
